<template>
    <div class="BinaryDataDisplay" :class="`BinaryDataDisplay--${type}`">
        <img v-if="type === 'image'" alt="" class="BinaryDataDisplay__image" :src="url">
        <iframe
            v-else
            class="BinaryDataDisplay__frame"
            :src="iframeUrl"
            frameborder="0"
        />
    </div>
</template>

<style lang="scss">
    .BinaryDataDisplay {
        display: flex;
        align-items: center;
        justify-content: center;

        &__image {
            display: block;
            max-width: 100%;
            margin: 0 auto;
        }

        &__frame {
            display: block;
            width: 100%;
            border: 0;
        }
    }
</style>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    export interface BinaryData {
        data: string;
        mimeType: string;
    }

    @Component
    export default class BinaryDataDisplay extends Vue {
        @Prop({ type: Object })
        private value!: BinaryData;

        @Prop({ type: String, default: 'FitH' })
        private pdfViewMode!: string;

        private type: string = 'image';
        private url: string | null = null;
        private currentDataHash: string | null = null;

        private get isPdf(): boolean {
            return this.value?.mimeType === 'application/pdf';
        }

        private get iframeUrl(): string {
            if (!this.url) {
                return '';
            }
            // Only apply PDF view parameters to PDFs
            if (this.isPdf) {
                return this.pdfViewMode === 'zoom'
                    ? `${this.url}#zoom=100`
                    : `${this.url}#view=${this.pdfViewMode}`;
            }
            return this.url;
        }

        private hashData(data: string): string {
            // Simple hash function for data comparison
            // Using a combination of length and first/last chars for quick comparison
            if (!data) {
                return '';
            }
            return `${data.length}-${data.substring(0, 10)}-${data.substring(data.length - 10)}`;
        }

        @Watch('value', { immediate: true, deep: true })
        private onBinaryDataChange(newValue: BinaryData): void {
            if (!newValue || !newValue.data || !newValue.mimeType) {
                this.clearBlob();
                return;
            }

            // Check if data actually changed to avoid unnecessary blob recreation
            const newHash = `${newValue.mimeType}-${this.hashData(newValue.data)}`;
            if (newHash === this.currentDataHash && this.url) {
                // Data hasn't changed, keep existing blob URL
                return;
            }

            // Store old URL before creating new one, then revoke it
            const oldUrl = this.url;
            if (oldUrl) {
                URL.revokeObjectURL(oldUrl);
            }

            // Compute document type
            this.type = /^image\//.test(newValue.mimeType) ? 'image' : 'other';

            // Build object URL
            try {
                const data = new Uint8Array([ ...atob(newValue.data) ].map((char) => char.charCodeAt(0)));
                const blob = new Blob([ data ], { type: newValue.mimeType });
                this.url = URL.createObjectURL(blob);
                this.currentDataHash = newHash;
            } catch (error) {
                console.error('Error creating blob URL:', error);
                this.url = null;
                this.currentDataHash = null;
            }
        }

        private unmounted(): void {
            this.clearBlob();
        }

        private clearBlob(): void {
            if (this.url !== null) {
                URL.revokeObjectURL(this.url);
                this.url = null;
            }
            this.currentDataHash = null;
        }
    }
</script>
